import { type DateTime } from 'luxon'

import { type BudgetSubmissionSchema } from '@webapp/api/BudgetCategoriesAPI'
import {
  type BudgetIntervalType,
  BudgetIntervalTypes,
} from '@webapp/models/enums/BudgetInterval'

/**
 * Calculates the number of intervals within a range of dates (0 indexed)
 * @param {DateTime} dateRangeStart
 * @param {DateTime} dateRangeEnd
 * @param {BudgetIntervalType} interval
 * @returns number
 */
export const calculateNumberOfPeriods = (
  dateRangeStart: DateTime,
  dateRangeEnd: DateTime,
  interval: BudgetIntervalType
): number => {
  switch (interval) {
    case BudgetIntervalTypes.YEARLY: {
      const { year: startYear } = dateRangeStart
      const { year: endYear } = dateRangeEnd
      return endYear - startYear + 1
    }

    case BudgetIntervalTypes.QUARTERLY: {
      const { year: startYear, quarter: startQuarter } = dateRangeStart
      const { year: endYear, quarter: endQuarter } = dateRangeEnd
      return endQuarter - startQuarter + (endYear - startYear) * 4 + 1
    }

    case BudgetIntervalTypes.MONTHLY: {
      const { year: startYear, month: startMonth } = dateRangeStart
      const { year: endYear, month: endMonth } = dateRangeEnd
      return endMonth - startMonth + (endYear - startYear) * 12 + 1
    }

    default:
      return 1
  }
}

/**
 * Generates monthly budgets for a range of dates.
 * For consistent budgets the amounts array must only have one element in it.
 * For custom budgets the amounts array must have a value for each budget and assumed to be in sequential order.
 * For custom budgets if a budget amount is missing then it will be set to null.
 * @param {DateTime} dateRangeStart
 * @param {DateTime} dateRangeEnd
 * @param {number[]} amounts - array length must be one or the number of monthly budgets in the interval
 * @returns
 */
export const generateMonthlyBudgets = (
  dateRangeStart: DateTime,
  dateRangeEnd: DateTime,
  amounts: number[]
): BudgetSubmissionSchema[] => {
  const monthlyBudgets = []
  const { month: startMonth, year: startYear } = dateRangeStart
  const { month: endMonth, year: endYear } = dateRangeEnd
  const totalMonths = calculateNumberOfPeriods(
    dateRangeStart,
    dateRangeEnd,
    BudgetIntervalTypes.MONTHLY
  )
  let currentMonth = startMonth
  let currentYear = startYear

  const generateMonthlyBudget = (amount: number): BudgetSubmissionSchema => {
    // first month of date range edge cases
    if (currentMonth === startMonth && currentYear === startYear) {
      // date range starts and ends in the same month of the same year
      if (startMonth === endMonth && endYear === startYear) {
        return {
          amount,
          start_datetime: dateRangeStart.toISO(),
          end_datetime: dateRangeEnd.toISO(),
        }
      }

      return {
        amount,
        start_datetime: dateRangeStart.toISO(),
        end_datetime: dateRangeStart.endOf('month').toISO(),
      }
    }

    // last month of date range edge case
    if (currentMonth === endMonth && currentYear === endYear) {
      return {
        amount,
        start_datetime: dateRangeEnd.startOf('month').toISO(),
        end_datetime: dateRangeEnd.toISO(),
      }
    }

    // middle months
    return {
      amount,
      start_datetime: dateRangeStart
        .set({ year: currentYear, month: currentMonth })
        .startOf('month')
        .toISO(),
      end_datetime: dateRangeStart
        .set({ year: currentYear, month: currentMonth })
        .endOf('month')
        .toISO(),
    }
  }

  let budgetAmount = null

  for (let budgetNumber = 0; budgetNumber < totalMonths; budgetNumber++) {
    if (amounts[budgetNumber]) {
      budgetAmount = amounts[budgetNumber]
    } else if (amounts.length > 1) {
      budgetAmount = null
    }

    monthlyBudgets.push(generateMonthlyBudget(budgetAmount))

    if (currentMonth === 12) {
      currentMonth = 1
      currentYear++
    } else {
      currentMonth++
    }
  }

  return monthlyBudgets
}

/**
 * Generates quarterly budgets for a range of dates.
 * For consistent budgets the amounts array must only have one element in it.
 * For custom budgets the amounts array must have a value for each budget and assumed to be in sequential order.
 * For custom budgets if a budget amount is missing then it will be set to null.
 * @param {DateTime} dateRangeStart
 * @param {DateTime} dateRangeEnd
 * @param {number[]} amounts - array length must be one or the number of quarterly budgets
 * @returns
 */
export const generateQuarterlyBudgets = (
  dateRangeStart: DateTime,
  dateRangeEnd: DateTime,
  amounts: number[]
): BudgetSubmissionSchema[] => {
  const quarterlyBudgets = []
  const { quarter: startQuarter, year: startYear } = dateRangeStart
  const { quarter: endQuarter, year: endYear } = dateRangeEnd
  const totalQuarters = calculateNumberOfPeriods(
    dateRangeStart,
    dateRangeEnd,
    BudgetIntervalTypes.QUARTERLY
  )
  let currentQuarter = startQuarter
  let currentYear = startYear
  let currentDate = dateRangeStart.startOf('quarter')

  const generateQuarterlyBudget = (amount: number): BudgetSubmissionSchema => {
    // first month of date range edge case
    if (currentQuarter === startQuarter && currentYear === startYear) {
      // date range starts and ends in the same month of the same year
      if (startQuarter === endQuarter && endYear === startYear) {
        return {
          amount,
          start_datetime: dateRangeStart.toISO(),
          end_datetime: dateRangeEnd.toISO(),
        }
      }

      return {
        amount,
        start_datetime: dateRangeStart.toISO(),
        end_datetime: dateRangeStart.endOf('quarter').toISO(),
      }
    }

    // last month of date range edge case
    if (currentQuarter === endQuarter && currentYear === endYear) {
      return {
        amount,
        start_datetime: dateRangeEnd.startOf('quarter').toISO(),
        end_datetime: dateRangeEnd.toISO(),
      }
    }

    // middle months
    return {
      amount,
      start_datetime: currentDate.startOf('quarter').toISO(),
      end_datetime: currentDate.endOf('quarter').toISO(),
    }
  }

  let budgetAmount = null

  for (let budgetNumber = 0; budgetNumber < totalQuarters; budgetNumber++) {
    if (amounts[budgetNumber]) {
      budgetAmount = amounts[budgetNumber]
    } else if (amounts.length > 1) {
      budgetAmount = null
    }

    quarterlyBudgets.push(generateQuarterlyBudget(budgetAmount))

    if (currentQuarter === 4) {
      currentQuarter = 1
      currentYear++
    } else {
      currentQuarter++
    }
    currentDate = currentDate.plus({ quarters: 1 })
  }

  return quarterlyBudgets
}

/**
 * Generates yearly budgets for a range of dates.
 * For consistent budgets the amounts array must only have one element in it.
 * For custom budgets the amounts array must have a value for each budget and assumed to be in sequential order.
 * For custom budgets if a budget amount is missing then it will be set to null.
 * @param {DateTime} dateRangeStart
 * @param {DateTime} dateRangeEnd
 * @param {number[]} amounts - array length must be one or the number of yearly budgets
 * @returns
 */
export const generateYearlyBudgets = (
  dateRangeStart: DateTime,
  dateRangeEnd: DateTime,
  amounts: number[]
): BudgetSubmissionSchema[] => {
  const yearlyBudgets = []
  const { year: startYear } = dateRangeStart
  const { year: endYear } = dateRangeEnd
  const totalYears = calculateNumberOfPeriods(
    dateRangeStart,
    dateRangeEnd,
    BudgetIntervalTypes.YEARLY
  )
  let currentYear = startYear

  const generateYearlyBudget = (amount: number): BudgetSubmissionSchema => {
    // date range start year edge cases
    if (currentYear === startYear) {
      // date range starts and ends in the same year
      if (startYear === endYear) {
        return {
          amount,
          start_datetime: dateRangeStart.toISO(),
          end_datetime: dateRangeEnd.toISO(),
        }
      }
      return {
        amount,
        start_datetime: dateRangeStart.toISO(),
        end_datetime: dateRangeStart.endOf('year').toISO(),
      }
    }

    // end edge case
    if (currentYear === endYear) {
      return {
        amount,
        start_datetime: dateRangeEnd.startOf('year').toISO(),
        end_datetime: dateRangeEnd.toISO(),
      }
    }

    // middle case
    return {
      amount,
      start_datetime: dateRangeStart
        .set({ year: currentYear })
        .startOf('year')
        .toISO(),
      end_datetime: dateRangeStart
        .set({ year: currentYear })
        .endOf('year')
        .toISO(),
    }
  }

  let budgetAmount = null

  for (let budgetNumber = 0; budgetNumber < totalYears; budgetNumber++) {
    if (amounts[budgetNumber]) {
      budgetAmount = amounts[budgetNumber]
    } else if (amounts.length > 1) {
      budgetAmount = null
    }

    yearlyBudgets.push(generateYearlyBudget(budgetAmount))

    currentYear++
  }

  return yearlyBudgets
}

/**
 * Generates the budget for an entire date range.
 * Only the first value in amounts will be used.
 * @param {DateTime} dateRangeStart
 * @param {DateTime} dateRangeEnd
 * @param {number[]} amounts
 * @returns
 */
export const generateBudgetForEntireDateRange = (
  dateRangeStart: DateTime,
  dateRangeEnd: DateTime,
  amounts: number[] // keeps consistent with other functions
): BudgetSubmissionSchema[] => {
  let amount = null
  if (amounts.length > 0) {
    amount = amounts[0]
  }

  return [
    {
      amount,
      start_datetime: dateRangeStart.toISO(),
      end_datetime: dateRangeEnd.toISO(),
    },
  ]
}
